import {
  ClickType,
  CompStatus,
  CompType,
  LoginType,
  RechargeType,
} from "./baseEnum";
import { GameItem, MakerItem } from "./baseType";

/** 基础组件属性接口 */
export interface BaseComponentAttrs {
  [key: string]: string | number | boolean | object | null | undefined;
}

/** 基础组件类 */
export abstract class BaseComponent<
  T extends BaseComponentAttrs = BaseComponentAttrs,
> {
  /** 组件ID */
  public id: string = "";
  /** 组件类型 */
  public type: CompType = CompType.none;
  /** 组件变体 */
  public variant: string = "";
  /** 标题图标 */
  public title_icon = "";
  /** 组件标题 */
  public title: string = "";
  /** 组件标识 */
  public key: string = "";
  /** 组件属性 */
  public attrs: T = {} as T;
  /** 新后台新增配置属性 */
  public lang_attrs: T = {} as T;
  /** 组件行为 */
  public actions: string = "";
  /** 组件样式 */
  public styles: string = ""; // 准备废弃
  /** 组件样式(新) */
  public style: number = 0;
  /** 组件状态 */
  public status: CompStatus = CompStatus.disable;
  /** 排序 */
  public sort: number = 0;
  /** 游戏列表 */
  public games: GameItem[] | null = null;
  /** 厂商列表 */
  public makers: MakerItem[] | null = null;
  /** 登录类型 */
  public login_type: LoginType = LoginType.None;
  /** 充值类型 */
  public recharge_type: RechargeType = RechargeType.None;
  /** 固定顶部 */
  public fixed_top: number = 0;
  /** 显示RTP */
  public show_rtp: boolean = false;
  /** 高度 */
  public height: string | number = "";
  /** 延迟时间 */
  public delay: number = 0;

  public game_ids: number[] = [];
  /** 点击ID */
  public click_id: ClickType = ClickType.None;
  /** 点击参数 */
  public click_params: string = "";

  /** 组件是否已销毁 */
  private _isDestroyed: boolean = false;

  constructor(data: Partial<BaseComponent<T>> = {}) {
    const keys = Object.keys(data) as Array<keyof typeof data>;

    keys.forEach((key) => {
      const value = data[key];
      if (value !== undefined) {
        (this as Record<string, unknown>)[key] = value;
      }
    });
  }

  /** 设置组件属性 */
  protected setAttrs(attrs: Partial<T>): void {
    this.attrs = { ...this.attrs, ...attrs };
  }

  /** 获取标题图标 */
  public getTitleIcon(): string {
    return this.title_icon || "";
  }

  /** 转换为JSON */
  public toJSON(): object {
    return {
      id: this.id,
      type: this.type,
      variant: this.variant,
      title: this.title,
      key: this.key,
      attrs: this.attrs,
      lang_attrs: this.lang_attrs,
      actions: this.actions,
      style: this.style,
      status: this.status,
      sort: this.sort,
      games: this.games,
      makers: this.makers,
      login_type: this.login_type,
      recharge_type: this.recharge_type,
      fixed_top: this.fixed_top,
      show_rtp: this.show_rtp,
      height: this.height,
      delay: this.delay,
      click_id: this.click_id,
      click_params: this.click_params,
    };
  }

  /** 销毁组件 */
  public destroy(): void {
    if (this._isDestroyed) return;

    // 清理游戏列表
    this.games = null;
    // 清理厂商列表
    this.makers = null;
    // 清理属性
    this.attrs = {} as T;
    // 清理新后台新增配置属性
    this.lang_attrs = {} as T;
    // 清理游戏ID列表
    this.game_ids = [];
    // 重置其他属性
    this.id = "";
    this.type = CompType.none;
    this.variant = "";
    this.title = "";
    this.key = "";
    this.actions = "";
    this.style = 0;
    this.status = CompStatus.disable;
    this.sort = 0;
    this.login_type = LoginType.None;
    this.recharge_type = RechargeType.None;
    this.fixed_top = 0;
    this.show_rtp = false;
    this.height = "";
    this.delay = 0;
    this.click_id = ClickType.None;
    this.click_params = "";

    this._isDestroyed = true;
  }

  /** 检查组件是否已销毁 */
  public isDestroyed(): boolean {
    return this._isDestroyed;
  }
}
